<template>
  <overlay-template
    :title="'Target annual revenue'"
    :primary-number="rate.goal.incoming"
    :secondary-number="rate.goal.outgoing"
    :secondary-number-title="'Estimated annual break-even'"
  >
    <div slot="intro">
      <p>Annual revenue is the amount of money that your company generates over the year.</p>
      <p>To capture a solid revenue target, take your new <em>Revenue per full-time employee</em> ({{ rate.goal.incomingPerEmployee | currency }}) and multiply it by the number of full-time employees you want ({{ rate.goal.fullTimeEmployees }}). This target also provide the perfect baseline to set your rates.</p>
    </div>
    <p slot="formula">{{ rate.goal.incomingPerEmployee | currency }} revenue per full-time employee goal &times; {{ rate.goal.fullTimeEmployees }} full-time employee goal = <strong>{{ rate.goal.incoming | currency }} target annual revenue</strong></p>
    <div slot="break-even-formula">
      <p>{{ rate.goal.outgoingPerEmployee | currency }} break-even per full-time employee goal &times; {{ rate.goal.fullTimeEmployees }} full-time employee goal = <strong>{{ rate.goal.outgoing | currency }} target break-even</strong></p>
    </div>
    <div
      slot="charts"
      class="grid-column-span-3_sm grid p0 mt3 grid-columns-2 grid-columns-4_md">
      <h2 class="grid-column-full">Incremental revenue targets</h2>
      <div>
        <h3>Monthly</h3>
        <div class="price extra-condensed font-size-7">{{ rate.goal.monthIncoming | currency }}</div>
        <div class="dark-red-300-text font-size-2 medium">{{ rate.goal.monthOutgoing | currency }} break even</div>
      </div>
      <div>
        <h3>Sprintly</h3>
        <div class="price extra-condensed font-size-7">{{ rate.goal.sprintIncoming | currency }}</div>
        <div class="dark-red-300-text font-size-2 medium">{{ rate.goal.sprintOutgoing | currency }} break even</div>
      </div>
      <div>
        <h3>Weekly</h3>
        <div class="price extra-condensed font-size-7">{{ rate.goal.weekIncoming | currency }}</div>
        <div class="dark-red-300-text font-size-2 medium">{{ rate.goal.weekOutgoing | currency }} break even</div>
      </div>
      <div>
        <h3>Daily</h3>
        <div class="price extra-condensed font-size-7">{{ rate.goal.dayIncoming | currency }}</div>
        <div class="dark-red-300-text font-size-2 medium">{{ rate.goal.dayOutgoing | currency }} break even</div>
      </div>

      <div class="grid-column-full"><hr class="grey-border border-width-1 solid-border my3"></div>
      <h2 class="grid-column-full">Money in vs. out</h2>
      <div class="mr1">
        <h5 class="semi-condensed">Current vs goals</h5>
        <lw-bar-chart :chart-data="annualRevenueGraph" />
      </div>
      <div class="mr1">
        <h5 class="semi-condensed">Profit/loss trend</h5>
        <lw-line-chart :chart-data="profitGrowthGraph" />
      </div>
      <div class="legend mb3">
        <h5 class="mb1">Revenue</h5>
        <div class="profit">Current<span class="block font-size-4 bold price">{{ rate.current.incoming | currency }}</span></div>
        <div class="profit">Goal <span class="block font-size-4 bold price">{{ rate.goal.incoming | currency }}</span></div>
      </div>
      <div class="legend mb3">
        <h5 class="mb1">Expenses</h5>
        <div class="expenses">Current <span class="block font-size-4 bold price">{{ rate.current.outgoing | currency }}</span></div>
        <div class="expenses">Goal <span class="block font-size-4 bold price">{{ rate.goal.outgoing | currency }}</span></div>
      </div>

      <div class="grid-column-full"><hr class="grey-border border-width-1 solid-border mb3"></div>
      <h2 class="grid-column-full">Target revenue breakdown</h2>
      <div class="">
        <h5 class="semi-condensed mb1">Current estimated revenue breakdown</h5>
        <lw-pie-chart :chart-data="currentRevenueBreakdownChart" />
      </div>
      <div class="legend align-self-end">
        <div class="salaries">Salaries estimate <span class="block font-size-4 bold price">{{ rate.current.salaries | currency }} <span class="bold font-size-2">{{ proportions.currentSalary }}%</span></span></div>
        <div class="expenses">Operating&nbsp;expenses estimate <span class="block font-size-4 bold price">{{ rate.current.expenses | currency }} <span class="bold font-size-2">{{ proportions.currentExpenses }}%</span></span></div>
        <div
          v-if="rate.current.incoming >= rate.current.outgoing"
          class="profit">Profit <span class="block font-size-4 bold price">{{ rate.current.profit | currency }} <span class="bold font-size-2">{{ proportions.currentProfit }}%</span></span></div>
        <div
          v-else
          class="defecit">Defecit <span class="block font-size-4 bold price">{{ rate.current.defecit | currency }} <span class="bold font-size-2">{{ proportions.currentDefecit }}%</span></span></div>
      </div>
      <div class="">
        <h5 class="semi-condensed mb1">Goal revenue breakdown</h5>
        <lw-pie-chart :chart-data="goalRevenueBreakdownChart" />
      </div>
      <div class="legend align-self-end">
        <div class="salaries">Salaries estimate <span class="block font-size-4 bold price">{{ rate.goal.salaries | currency }} <span class="bold font-size-2">{{ proportions.salary }}%</span></span></div>
        <div class="expenses">Operating&nbsp;expenses estimate <span class="block font-size-4 bold price">{{ rate.goal.expenses | currency }} <span class="bold font-size-2">{{ proportions.expenses }}%</span></span></div>
        <div class="profit">Profit <span class="block font-size-4 bold price">{{ rate.goal.profit | currency }} <span class="bold font-size-2">{{ proportions.profit }}%</span></span></div>
      </div>
    </div>
  </overlay-template>
</template>

<script>
import OverlayTemplate from './OverlayTemplate.vue'
import BarChart from '../../livewires/BarChart.vue'
import LineChart from '../../livewires/LineChart.vue'
import PieChart from '../../livewires/PieChart.vue'

export default {
  components: {
    overlayTemplate: OverlayTemplate,
    lwBarChart: BarChart,
    lwLineChart: LineChart,
    lwPieChart: PieChart
  },
  data () {
    return {
      page: this.$route.params.id
    }
  },
  computed: {
    rate () {
      return this.$store.getters.rateCard(this.page)
    },
    proportions () {
      return this.$store.getters.revenueProportions(this.page)
    },
    annualRevenueGraph () {
      return this.$store.getters.annualRevenueGraph(this.page)
    },
    profitGrowthGraph () {
      return this.$store.getters.profitGrowthGraph(this.page)
    },
    currentRevenueBreakdownChart () {
      return this.$store.getters.currentRevenueBreakdownChart(this.page)
    },
    goalRevenueBreakdownChart () {
      return this.$store.getters.goalRevenueBreakdownChart(this.page)
    }
  }
}
</script>
